import {IQueryKeysNamesResolver} from '../../@interfaces/services/NameResolvers/IQueryKeysNamesResolver';
import {QueryKey} from '@tanstack/react-query';

export default class QueryKeysNameResolver implements IQueryKeysNamesResolver {
    static FAKE_REQUEST_TAG = '@fake';
    static CUSTOM_REQUEST_TAG = '@custom';

    static LIST_TAG = 'list-of-records';
    static DETAILS_TAG = 'details-of-record';

    getGlobalQueryKey(...keys: unknown[]): QueryKey {
        return this.resolve(keys);
    }

    getFakeRequestQueryKey(...keys: unknown[]): QueryKey {
        return this.resolve(
            this.getGlobalQueryKey(),
            QueryKeysNameResolver.FAKE_REQUEST_TAG,
            ...keys
        );
    }

    getCustomRequestQueryKey(...keys: unknown[]): QueryKey {
        return this.resolve(
            this.getGlobalQueryKey(),
            QueryKeysNameResolver.CUSTOM_REQUEST_TAG,
            ...keys
        );
    }

    getCustomQueryOfExactModelQueryKey(modelName: string, modelId?: unknown, ...keys: unknown[]): QueryKey {
        return this.getCustomRequestQueryKey(
            this.getModelKey(modelName),
            this.getModelIdKey(modelName, modelId),
            ...keys
        );
    }

    getAllQueriesOfExactModelQueryKey(modelName: string, modelId?: unknown, ...keys: unknown[]): QueryKey {
        return this.getGlobalQueryKey(
            this.getModelKey(modelName),
            ...(modelId === undefined ? keys : [this.getModelIdKey(modelName, modelId), ...keys])
        );
    }

    getAllListsOfRecordsQueryKey(filters?: unknown, uniqueShape?: unknown): QueryKey {
        return this.resolve(
            this.getGlobalQueryKey(),
            QueryKeysNameResolver.LIST_TAG,
            this.resolve(filters),
            this.resolve(uniqueShape)
        );
    }

    getListOfExactModelRecordsQueryKey(modelName: string, filters?: unknown, uniqueShape?: unknown): QueryKey {
        return this.resolve(
            this.getAllListsOfRecordsQueryKey(),
            this.getModelKey(modelName),
            typeof filters === 'string' ? filters : [filters],
            this.resolve(uniqueShape)
        );
    }

    getCustomListOfExactModelRecordsQueryKey(modelName: string, filters?: unknown, ...keys: unknown[]): QueryKey {
        return this.resolve(
            this.getAllListsOfRecordsQueryKey(),
            this.getModelKey(modelName),
            typeof filters === 'string' ? filters : [filters],
            ...keys,
        );
    }

    getAllDetailsOfRecordsQueryKey(filters?: unknown, uniqueShape?: unknown): QueryKey {
        return this.resolve(
            this.getGlobalQueryKey(),
            QueryKeysNameResolver.DETAILS_TAG,
            this.resolve(filters),
            this.resolve(uniqueShape)
        );
    }

    getAllDetailsOfExactModelQueryKey(modelName: string, filters?: unknown, uniqueShape?: unknown): QueryKey {
        return this.resolve(
            this.getAllDetailsOfRecordsQueryKey(),
            this.getModelKey(modelName),
            this.resolve(filters),
            this.resolve(uniqueShape)
        );
    }

    getDetailsOfRecordQueryKey(modelName: string, modelId: unknown, filters?: unknown, uniqueShape?: unknown): QueryKey {
        return this.resolve(
            this.getAllDetailsOfExactModelQueryKey(modelName),
            this.getModelIdKey(modelName, modelId),
            this.resolve(filters),
            this.resolve(uniqueShape)
        );
    }

    protected getModelKey(modelName: string): string {
        return `model:${modelName}`;
    }

    protected getModelIdKey(modelName: string, modelId: unknown): string | undefined {
        if (modelId === undefined) {
            return undefined;
        }

        return `id:${modelName}@${modelId}`;
    }

    protected resolve(...items: unknown[]): QueryKey[] {
        const result: QueryKey[] = [];
        const existsTags: Record<string, number> = {};

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item === undefined) {
                continue;
            }

            (Array.isArray(item) ? item : [item]).forEach(one => {
                const isTag = typeof one === 'string';
                if (isTag && one in existsTags) {
                    return;
                }

                const index = result.push(one) - 1;
                if (isTag) {
                    existsTags[one] = index;
                }
            });
        }

        return this.sortKeys(result);
    }

    protected sortKeys(keys: QueryKey[]): QueryKey[] {
        return keys.sort((a, b) => {
            return this.getWeightOfKey(a) - this.getWeightOfKey(b);
        });
    }

    protected getWeightOfKey(key: unknown): number {
        if (typeof key !== 'string') {
            return 0;
        }

        if ([QueryKeysNameResolver.FAKE_REQUEST_TAG, QueryKeysNameResolver.CUSTOM_REQUEST_TAG].includes(key)) {
            return -110;
        }

        if (key.startsWith('model:')) {
            return -100;
        }

        if (key.startsWith('id:')) {
            return -90;
        }

        return 0;
    }
}
